import React, { useEffect, useRef } from 'react';
import { Box, FormControl, InputLabel, Select, MenuItem, Typography, TextField, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

const formatDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const TechActionStatFilter = ({
  dptUuid = '',
  setDptUuid,
  usrUuid = '',
  setUsrUuid,
  startYear,
  setStartYear,
  startMonth,
  setStartMonth,
  endYear,
  setEndYear,
  endMonth,
  setEndMonth,
  dptList = [],
  usrList = [],
  onFetchStat
}) => {
  const months = Array.from({ length: 12 }, (_, i) => i + 1);

  const dptListLength = dptList?.length || 0;
  useEffect(() => {
    if (dptList && dptList.length > 0 && !dptUuid) {
      const firstDpt = dptList;
      if (firstDpt && firstDpt.dptUuid) {
        setDptUuid(firstDpt.dptUuid);
      }
    }
  }, [dptListLength, dptUuid, setDptUuid]);

  const filteredUsrList = React.useMemo(() => {
    if (!dptUuid) return [];
    return usrList.filter(usr => usr.dptUuid === dptUuid);
  }, [dptUuid, usrList]);

  const handleStartYearChange = (newYear) => {
    setStartYear(newYear);
    if (newYear > endYear || (newYear === endYear && startMonth > endMonth)) {
      setEndYear(newYear);
      setEndMonth(startMonth);
    }
  };

  const handleStartMonthChange = (newMonth) => {
    setStartMonth(newMonth);
    if (startYear === endYear && newMonth > endMonth) {
      setEndMonth(newMonth);
    }
  };

  const handleEndYearChange = (newYear) => {
    setEndYear(newYear);
    if (newYear < startYear || (newYear === startYear && endMonth < startMonth)) {
      setStartYear(newYear);
      setStartMonth(endMonth);
    }
  };

  const handleEndMonthChange = (newMonth) => {
    setEndMonth(newMonth);
    if (startYear === endYear && newMonth < startMonth) {
      setStartMonth(newMonth);
    }
  };

  const localUser = localStorage.getItem('user');
  let loginUsrUuid = '';
  if (localUser) {
      const parsedUser = JSON.parse(localUser);
      loginUsrUuid = parsedUser.usrUuid || parsedUser.userUuid || '';
  }

  const isFirstRender = useRef(true);
  const prevArgsRef = useRef('');

  useEffect(() => {
    if (!startYear || !endYear) return;

    const startStr = `${startYear}-${String(startMonth).padStart(2, '0')}-01`;
    const endStr = `${endYear}-${String(endMonth).padStart(2, '0')}-${String(new Date(endYear, endMonth, 0).getDate()).padStart(2, '0')}`;

    const loginUuidParam = (!dptUuid && !usrUuid) ? loginUsrUuid : '';

    let metaData = { 
      type: '', 
      dptInfo: null, 
      list: [] 
    };

    if (!dptUuid) {
      metaData = {
        type: 'DPT_LIST',
        dptInfo: { id: '', nm: '전체 부서' },
        list: dptList
          .filter(dpt => dpt.dptUuid)
          .map(dpt => ({ id: dpt.dptUuid, nm: dpt.dptNm }))
      };
    } else {
      const selectedDptObj = dptList.find(dpt => dpt.dptUuid === dptUuid);
      const currentDptInfo = {
        id: dptUuid,
        nm: selectedDptObj ? selectedDptObj.dptNm : ''
      };

      if (!usrUuid) {
        metaData = {
          type: 'USR_LIST',
          dptInfo: currentDptInfo,
          list: filteredUsrList.map(usr => ({ id: usr.usrUuid, nm: usr.usrNm }))
        };
      } else {
        const selectedUsr = filteredUsrList.find(usr => usr.usrUuid === usrUuid);
        metaData = {
          type: 'SINGLE_USR',
          dptInfo: currentDptInfo,
          list: selectedUsr ? [{ id: selectedUsr.usrUuid, nm: selectedUsr.usrNm }] : []
        };
      }
    }

    const currentArgsKey = JSON.stringify({ startStr, endStr, dptUuid, usrUuid, loginUuidParam, metaData });
    if (prevArgsRef.current === currentArgsKey) {
      return;
    }
    prevArgsRef.current = currentArgsKey;

    if (isFirstRender.current) {
        onFetchStat(startStr, endStr, dptUuid, usrUuid, loginUuidParam, metaData);
        if (dptListLength > 0) {
            isFirstRender.current = false;
        }
    } else {
        onFetchStat(startStr, endStr, dptUuid, usrUuid, loginUuidParam, metaData);
    }
    
  }, [startYear, startMonth, endYear, endMonth, dptUuid, usrUuid, onFetchStat, dptListLength, loginUsrUuid, dptList, filteredUsrList]);

  const handleYearChange = (setter) => (e) => {
    const val = e.target.value.replace(/[^0-9]/g, '');
    setter(val ? Number(val) : '');
  };

  const handleYearBlur = (setter, fallbackYear) => () => {
    setter(prev => (prev && prev >= 1900) ? prev : fallbackYear);
  };

  const renderYearStepper = (yearVal, onYearChange, fallbackYear) => (
    <Box 
      sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        border: '1px solid #ccc', 
        borderRadius: '4px', 
        height: '40px', 
        px: 0.5, 
        backgroundColor: '#ffffff',
        '&:hover': { borderColor: '#333333' }
      }}
    >
      <IconButton size="small" onClick={() => onYearChange(Math.max(1900, Number(yearVal) - 1))} sx={{ p: '4px' }}>
        <RemoveIcon fontSize="small" />
      </IconButton>
      <TextField
        variant="standard"
        value={yearVal}
        onChange={handleYearChange(onYearChange)}
        onBlur={handleYearBlur(onYearChange, fallbackYear)}
        inputProps={{ style: { textAlign: 'center', width: '45px', fontSize: '14px', fontWeight: '500', padding: 0 } }}
        InputProps={{ disableUnderline: true }}
      />
      <Typography variant="body2" sx={{ pr: 0.5, color: 'text.secondary', userSelect: 'none', fontSize: '14px' }}>년</Typography>
      <IconButton size="small" onClick={() => onYearChange(Number(yearVal) + 1)} sx={{ p: '4px' }}>
        <AddIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  return (
    <Box 
      sx={{ 
        p: 2, 
        mb: 1.5, 
        backgroundColor: '#ffffff', 
        borderRadius: '12px',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)', 
        border: '1px solid rgba(0, 0, 0, 0.03)',
        display: 'flex', 
        gap: 1.5, 
        alignItems: 'center', 
        flexWrap: 'wrap'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {renderYearStepper(startYear, handleStartYearChange, new Date().getFullYear())}
        <FormControl size="small" sx={{ width: 85 }}>
          <Select value={startMonth} onChange={(e) => handleStartMonthChange(e.target.value)}>
            {months.map(m => <MenuItem key={m} value={m}>{m}월</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      <Typography variant="body2" sx={{ color: 'text.disabled', fontWeight: '700', px: 0.2 }}>~</Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {renderYearStepper(endYear, handleEndYearChange, new Date().getFullYear())}
        <FormControl size="small" sx={{ width: 85 }}>
          <Select value={endMonth} onChange={(e) => handleEndMonthChange(e.target.value)}>
            {months.map(m => <MenuItem key={m} value={m}>{m}월</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ width: '1px', height: '24px', backgroundColor: '#e0e0e0', mx: 0.5 }} />

      <FormControl size="small" sx={{ minWidth: 120 }}>
        <InputLabel shrink id="dpt-select-label">부서</InputLabel>
        <Select 
          labelId="dpt-select-label"
          value={dptUuid} 
          label="부서" 
          displayEmpty
          notched={true}
          onChange={(e) => { setDptUuid(e.target.value); setUsrUuid(''); }}
        >
          {dptList.map(dpt => <MenuItem key={dpt.dptUuid} value={dpt.dptUuid}>{dpt.dptNm}</MenuItem>)}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 120 }}>
        <InputLabel shrink id="usr-select-label">담당자</InputLabel>
        <Select 
          labelId="usr-select-label"
          value={usrUuid} 
          label="담당자" 
          displayEmpty
          notched={true}
          disabled={!dptUuid}
          onChange={(e) => setUsrUuid(e.target.value)}
        >
          <MenuItem value="">전체 담당자</MenuItem>
          {filteredUsrList.map(usr => <MenuItem key={usr.usrUuid} value={usr.usrUuid}>{usr.usrNm}</MenuItem>)}
        </Select>
      </FormControl>
    </Box>
  );
};

export default TechActionStatFilter;
